import {
  Alert,
  Box,
  Button,
  Checkbox,
  IconButton,
  ListItemText,
  MenuItem,
  MenuList,
  Modal,
  Popover,
  Select,
  OutlinedInput,
  Tooltip
} from '@mui/material';
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import CustomDialog, {
  CLINICAL_RECORD_DIALOG_WIDTH
} from 'src/components/UI/dialog/CustomDialog';
import RecordBrowserModal from './RecordBrowserModal';
import RecordBrowser from './RecordBrowser';
import { createPortal } from 'react-dom';
import { Grid, Chip } from '@mui/material';
import TileCard from './NoteTile';
import { makeRecordOpeners } from './recordOpeners';
import {
  UNFIT_LINK_MESSAGES,
  hasLabReportInboxParam,
  labReportInboxLinkStep,
  readLabReportInboxLink,
  withoutLabReportInboxItem
} from './labReportInboxLink';
import { fetusSummaryRows } from '../../../pedigree-tool/pedigree/fetusEventSubject';
import { getRecordDate, readRecordDate } from './forms/formService';
import { toLocalDateFromInstant } from 'src/util/partialDate';
import { meaningOf } from 'src/util/statusSemantics';
import { SIDEBAR_VALUE } from '../../sidebarValues';

import axios from 'src/axios';

import {
  AddressForm,
  AppointmentForm,
  ClinicalNotesForm,
  ConsentForm,
  ContactForm,
  ReferralForm,
  SampleForm,
  TestForm,
  TreatmentForm,
  TissuePathologyForm,
  ProcessForm,
  RiskFactorForm
} from './forms/FormsExport';

import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import moment from 'moment';
import { AppContext, SnackContext, ThemeContext } from 'src/store/ContextStore';
import { formatMemberConsultationDates } from 'src/components/pedigree-chart/pedigree-tool/pedigree/consultationDates';
import HistoryIcon from '@mui/icons-material/History';
import { config as reproConfig } from '../../../pedigree-tool/pedigree/config';
import FilterListIcon from '@mui/icons-material/FilterList';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import { EVENTS } from 'src/components/admin/appReducer';

import TemplateStore from 'src/store/TemplateStore';
import UnsavedChangesDialog from 'src/components/UI/dialog/UnsavedChangesDialog';
import { useUnsavedFormGuard } from 'src/components/UI/input/changed-field';
import { formatPersonSummary } from '../../util';
import {
  recordAddressFor,
  isUnsavedMember,
  memberMatchKeyFor,
  hydrateMember,
  NO_MEMBER
} from './recordAddress';
import { recordSubject } from './recordSubject';
import { isRecordForMember, attendeeOptions } from './appointmentAttendees';
import { routeForType, canOpenType, CREATABLE_TYPES } from './recordRouting';
import { isAnonymousActive } from 'src/components/pedigree-chart/anonymousPedigrees';
import ModalHeader from '../diagnoses-menu/ModalHeader';
import { withFreshestGenotypes } from '../genotypes-menu/freshestGenotypes';
import { SuggestionFamilyContext } from 'src/components/UI/clinicalSuggestions/SuggestionFamilyContext';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// The record dialog's body while a Test's lab report is checked beside it, full screen (TGCLOUD-1868):
// a column the Test's two panes fill.
const FULL_SCREEN_BODY_STYLE = Object.freeze({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  minHeight: 0
});

const CREATABLE_FROM_EMPTY = [
  'Referral',
  'Appointment',
  'Clinical Note',
  'Sample',
  'Test'
];

function ClinicalNotes({
  // Taken as `dataProp` and re-derived below. See withFreshestGenotypes.
  data: dataProp,
  modificationHistoryHandle,
  memberRecords,
  deepCopy,
  setMemberRecords,
  isDisabled: isLocked,
  // A request from the Reproductive section to open a record scoped to one fetus (decision 21).
  // Cleared once acted on, so returning to this section later does not reopen the form.
  addFetusEvent,
  onAddFetusEventHandled,
  // (sectionValue, sidebarPatch?) => void. How the record browser sends a
  // clinician to the section that owns a record it cannot edit itself — a
  // disease, a phenotype, a genotype or a pregnancy. Expands that accordion,
  // and for a pregnancy also seeds the Reproductive section's `openRecordId`.
  // Optional: a surface without a sidebar simply offers no handoff.
  onNavigateToSection,
  // TGCLOUD-1669 — (sectionKey, facts) => void, so the collapsed row can show
  // a summary of records this component already holds. Optional: the legacy
  // patient sidebar renders this component without it.
  reportSummary,
  // TGCLOUD-1669 Phase 5 — where to render the DOCKED record browser, when the
  // drawer is promoted. A portal target rather than a copy of the records:
  // the records live here, this component is mounted whatever the accordion
  // state, and a second copy in the sidebar's state would be one more thing to
  // keep in step.
  dockNode,
  // TGCLOUD-1669 — a nonce the pinned header bumps to open the record browser.
  // The browser reads `tiles`, which live here, and this component is mounted
  // whatever the accordion state, so the header can open it without the
  // records having to move or be fetched twice.
  openBrowserSignal
}) {
  const t = useAutoTranslation();

  const { content, dispatchUpdate } = React.useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;
  const { familyId } = user;
  const { theme } = React.useContext(ThemeContext);
  const anonymousActive = isAnonymousActive(content?.settings?.pedigreeSetting);

  let [isModalOpen, setOpenModal] = React.useState(false);
  const [isBrowserOpen, setIsBrowserOpen] = React.useState(false);

  // Ignores the initial value so the browser does not spring open on mount.
  const lastBrowserSignal = useRef(openBrowserSignal);
  useEffect(() => {
    if (openBrowserSignal === lastBrowserSignal.current) return;
    lastBrowserSignal.current = openBrowserSignal;
    setIsBrowserOpen(true);
  }, [openBrowserSignal]);
  const [sortOrder, setSortOrder] = React.useState('newest');
  const browseAllRef = useRef(null);
  const familyMembers = React.useMemo(() => {
    if (Array.isArray(memberRecords)) return memberRecords;
    if (Array.isArray(deepCopy)) return deepCopy;
    return [];
  }, [memberRecords, deepCopy]);
  // Two enrichments of the same selection snapshot, composed in this order.
  //
  // `hydrateMember` fills the snapshot out from the family list, which also
  // covers the `deepCopy` fallback and the match-key path for a member with no
  // pid. `withFreshestGenotypes` then re-applies the TGCLOUD-1837 rule on top:
  // `dataProp` is taken once by ChartBox when a node is selected and never
  // re-derives, but `GET /genotypes` backfills ClinVar provenance behind the
  // user's back and refreshes the SHARED copy — so a letter built from a stale
  // snapshot would print a provenance line that was already out of date. Only
  // `genotypes` is taken from the shared copy; everything else is left as
  // hydration produced it.
  const data = React.useMemo(
    () =>
      withFreshestGenotypes(
        hydrateMember(dataProp, familyMembers),
        memberRecords
      ),
    [dataProp, familyMembers, memberRecords]
  );

  // TGCLOUD-1758 — the persisted record address, whether this person is saved
  // yet, and the separate key used only for in-memory matching. The reasoning
  // for keeping those three apart is in recordAddress.js.
  const familyMemberId = recordAddressFor(data);
  const memberIsUnsaved = isUnsavedMember(data);
  const memberMatchKey = memberMatchKeyFor(data);

  let [unsavedChanges, setUnsavedChanges] = React.useState({
    status: false,
    edit: false
  });
  // How many fields of the open record hold an unsaved change, as its form
  // compares them (TGCLOUD-1902). What closing it asks by: Cancel, the X,
  // Escape and a click outside alike.
  const [noteChangeCount, setNoteChangeCount] = React.useState(0);
  let [noteType, setNoteType] = React.useState('Referral');
  let [isEditing, setIsEditing] = React.useState(false);
  let [currentModel, setCurrentModel] = React.useState({});
  const [staffMembers, setStaffMembers] = useState([]);

  const [filter, setFilter] = React.useState({
    Referral: true,
    Appointment: true,
    Contact: true,
    Consent: true,
    Sample: true,
    Test: true,
    Treatment: true,
    Address: true,
    'Clinical Note': true,
    'Tissue Pathology': true,
    Process: true,
    'Risk Score': true
  });

  let [tiles, setTiles] = React.useState([]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const familyMemberIdFromURL = queryParams.get('familyMemberId');
  const appointmentIdFromURL = queryParams.get('appointmentId');
  const referralIdFromURL = queryParams.get('referralId');
  const contactIdFromURL = queryParams.get('contactId');
  const sampleIdFromURL = queryParams.get('sampleId');
  const testIdFromURL = queryParams.get('testId');
  const clinicalNoteIdFromURL = queryParams.get('clinicalNoteId');
  const consentIdFromURL = queryParams.get('consentId');
  const isOpenedModal = useRef(false);
  const navigate = useNavigate();
  const { setSnack } = React.useContext(SnackContext) || {};
  // THE LAB REPORT INBOX'S LINK (TGCLOUD-1866 Phase 3, labReportInboxLink.js). With an inbox item
  // in the address, the Test is opened by the link's own step below, once the PERSON is selected,
  // never by the record list's auto-open (which opens it for nobody). The item rides into the one
  // Test form opened for it, and no further.
  const labReportInboxInURL = hasLabReportInboxParam(location.search);
  const labReportInboxLink = React.useMemo(
    () => readLabReportInboxLink(location.search),
    [location.search]
  );
  const [labReportInboxItemId, setLabReportInboxItemId] = useState(null);
  // The person of a report about several the item is handed over for (TGCLOUD-1868, Phase E4: the
  // link's `individual`, a fetus's with its fetus record), or null for a report about one person.
  const [labReportInboxIndividual, setLabReportInboxIndividual] =
    useState(null);
  // THE TEST'S VERIFICATION VIEW (TGCLOUD-1868, plan D2): the Test dialog fills the window while a
  // lab report is checked beside the Test. TestForm says when (onVerificationModeChange: 'review'
  // once a report it read has a reading, 'saved' while a saved Test's report is open, null
  // otherwise), and the same TestForm stays mounted, so a Test that switches mid-session (a report
  // uploaded on it) keeps everything in it. Undefined until the open Test has said: a Test opened
  // with an inbox hand-off is full screen from the start, since it is opened to check a report. A
  // Test with no report keeps the 850px dialog.
  const [testViewMode, setTestViewMode] = useState(undefined);
  // The family whose records the list last finished loading, so the link knows they are in.
  const [recordsLoadedFor, setRecordsLoadedFor] = useState(null);
  const handledInboxItem = useRef(null);

  let [isLoading, setIsLoading] = React.useState(false);

  useEffect(() => {
    const loadTemplates = async () => {
      const store = TemplateStore.getInstance();
      await store.fetchTemplates();
    };
    loadTemplates();
  }, []);

  const staffMemberOptions = React.useMemo(() => {
    return staffMembers.map((d) => ({
      label: d.firstName + ' ' + d.lastName,
      value: d.userId
    }));
  }, [staffMembers]);

  const personLabel = React.useMemo(() => {
    const details = [user, ...(users?.family || [])].filter(Boolean);
    const members = familyMembers.length ? familyMembers : details;
    const labels = new Map(
      attendeeOptions(members, NO_MEMBER, details).map((o) => [
        o.value,
        o.label
      ])
    );
    return (id) => labels.get(String(id ?? '').trim());
  }, [familyMembers, user, users?.family]);

  const memberTiles = React.useMemo(
    () => tiles.filter((t) => isRecordForMember(t, familyMemberId)),
    [tiles, familyMemberId]
  );

  const memberTests = React.useMemo(
    () => memberTiles.filter((t) => t.noteType === 'Test'),
    [memberTiles]
  );

  const memberRecordsByType = React.useMemo(() => {
    const by = {};
    for (const t of memberTiles) {
      if (!t || !t.noteType) continue;
      (by[t.noteType] = by[t.noteType] || []).push(t);
    }
    return by;
  }, [memberTiles]);

  const clinicalDataTiles = React.useMemo(() => {
    const result = [];
    (data?.diagnoses || []).forEach((d) => {
      result.push({
        _id: d._id || `disease-${d.id}`,
        noteType: 'Disease',
        familyMemberId,
        name: d.name,
        id: d.id,
        age: d.age,
        dateReported: d.dateReported,
        clinicallyConfirmed: d.clinicallyConfirmed,
        createdAt: d.dateReported || d.createdAt
      });
    });
    (data?.phenotypes || []).forEach((p) => {
      result.push({
        _id: p._id || `phenotype-${p.id}`,
        noteType: 'Phenotype',
        familyMemberId,
        name: p.name,
        id: p.id,
        dateRecorded: p.dateRecorded,
        recordedAge: p.recordedAge,
        clinicallyConfirmed: p.clinicallyConfirmed,
        createdAt: p.dateRecorded || p.createdAt
      });
    });
    (data?.genotypes || []).forEach((g) => {
      result.push({
        _id: g._id || `genotype-${g.id}`,
        noteType: 'Genotype',
        familyMemberId,
        name: g.name,
        id: g.id,
        dateRecorded: g.dateRecorded,
        recordedAge: g.recordedAge,
        clinicallyConfirmed: g.clinicallyConfirmed,
        carrierStatus: g.carrierStatus,
        createdAt: g.dateRecorded || g.createdAt
      });
    });
    return result;
  }, [data?.diagnoses, data?.phenotypes, data?.genotypes, familyMemberId]);

  // Reproductive records (Track B) — surfaced in Browse All Records alongside the clinical
  // notes so a clinician can scan all clinically relevant history in one place. Gated on the
  // repro-nodes flag; view-only here (edit via the Reproductive accordion / pedigree).
  const reproTiles = React.useMemo(() => {
    if (!reproConfig.ENABLE_REPRO_NODES) return [];
    return (data?.pregnancies || []).map((p) => ({
      _id: p._id || `pregnancy-${p.id}`,
      noteType: 'Pregnancy',
      familyMemberId,
      outcome: p.outcome,
      gestationalWeeks: p.gestationalWeeks,
      conceivedWith: p.conceivedWith,
      fetalSexKnown: p.fetalSexKnown,
      fetalSex: p.fetalSex,
      karyotype: p.karyotype,
      comments: p.comments,
      hidden: p.hidden,
      createdAt: p.createdAt,
      // The fields above mirror only the FIRST fetus (they predate the two-level model), so a multiple
      // would list one fetus and hide the rest. Empty for a single-fetus pregnancy, where those fields
      // already say everything.
      fetusRows: fetusSummaryRows(p, t)
    }));
  }, [data?.pregnancies, familyMemberId, t]);

  const user_id = user?.user_id || user?._id;
  const [riskRecords, setRiskRecords] = React.useState([]);
  const fetchRiskRecords = React.useCallback(() => {
    if (!user_id) return;
    axios
      .get(APICONSTANTS.getRiskRecords.replace(':user_id', user_id))
      .then(({ data: res }) => setRiskRecords(Array.isArray(res) ? res : []))
      .catch(() => {});
  }, [user_id]);
  React.useEffect(() => {
    fetchRiskRecords();
  }, [fetchRiskRecords]);

  React.useEffect(() => {
    if (!isModalOpen && noteType === 'Risk Factor') fetchRiskRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  const riskTiles = React.useMemo(() => {
    const RISK_LABELS = {
      gail: 'GAIL',
      canrisk: 'CanRisk',
      prs: 'PRS',
      questionnaire: 'Questionnaire'
    };

    const memberIds = [data?.proBandId, data?.familyMemberId]
      .filter(Boolean)
      .map(String);
    return riskRecords
      .filter(
        (r) =>
          memberIds.length === 0 || memberIds.includes(String(r.familyMemberId))
      )
      .map((r) => ({
        _id: r._id,
        noteType: 'Risk Score',
        familyMemberId,
        recordType: r.recordType,
        riskLabel: RISK_LABELS[r.recordType] || r.recordType,
        gailRiskScore: r.gailRiskScore,
        canriskResult: r.canriskResult,
        prsResult: r.prsResult,
        createdAt: r.createdAt
      }));
  }, [riskRecords, familyMemberId, data?.proBandId, data?.familyMemberId]);

  // Everything the record browser lists, which is NOT the same set as the
  // CLINICAL RECORDS section: it also carries diseases, phenotypes, genotypes
  // and risk scores. One memo so the count on the header control and the rows
  // in the browser can never disagree.
  const browserTiles = React.useMemo(
    () => [...memberTiles, ...clinicalDataTiles, ...riskTiles, ...reproTiles],
    [memberTiles, clinicalDataTiles, riskTiles, reproTiles]
  );

  // TGCLOUD-1669 Phase 1 — report the collapsed-row summary upward.
  //
  // The records are fetched on mount (this component is mounted whatever its
  // accordion state), but they live in `tiles` here, where the row rendering
  // the summary cannot reach them. Nothing new is fetched; the answer is just
  // handed up.
  //
  // The count deliberately EXCLUDES diseases, phenotypes, genotypes and risk
  // scores. This section lists clinical notes; the record browser lists every
  // record type, so the same patient reads 37 here and 60 there. That is not
  // records going missing.
  const summaryDateFormat = theme?.dateFormat || 'DD/MM/YYYY';
  React.useEffect(() => {
    if (!reportSummary) return;
    if (isLoading) {
      // Not "none recorded": we do not yet know whether there are any.
      reportSummary(SIDEBAR_VALUE.clinicalNotes, { status: 'pending' });
      return;
    }
    const byType = {};
    Object.keys(memberRecordsByType).forEach((t) => {
      byType[t] = memberRecordsByType[t].length;
    });

    // Last and next are kept apart. A single "latest" that resolved to a booked
    // appointment reads as a record from the future.
    const today = moment().endOf('day');
    let last = null;
    let next = null;
    // Each record's day, read by how its date was written (readRecordDate): an
    // imported date is UTC midnight, which moment(raw) read as the day before
    // at a negative offset — in the summary, and in which side of today it
    // fell. An undated record falls back to when it was created, an instant.
    memberTiles.forEach((t) => {
      const day = getRecordDate(t)
        ? readRecordDate(t)
        : toLocalDateFromInstant(t.createdAt);
      if (!day) return;
      const m = moment(day);
      if (m.isAfter(today)) {
        if (!next || m.isBefore(next)) next = m;
      } else if (!last || m.isAfter(last)) {
        last = m;
      }
    });

    // A person's history of missed appointments is what Kathryn asked to be
    // able to see (TGCLOUD-1751). Read from what each status MEANS, so a
    // site's own wording for it counts too. From records already fetched.
    const didNotAttend = (memberRecordsByType.Appointment || []).filter(
      (t) =>
        meaningOf('appointmentStatuses', t.appointmentStatus) ===
        'did_not_attend'
    ).length;

    reportSummary(SIDEBAR_VALUE.clinicalNotes, {
      status: 'ready',
      count: memberTiles.length,
      byType,
      didNotAttend,
      lastDate: last ? last.format(summaryDateFormat) : null,
      nextDate: next ? next.format(summaryDateFormat) : null,
      // What the record BROWSER would list, which includes diseases,
      // phenotypes, genotypes and risk scores and so is a larger number than
      // `count`. The header control shows this one; the section summary shows
      // `count`. They are labelled differently so neither reads as the other
      // going missing.
      browserCount: browserTiles.length
    });
  }, [
    reportSummary,
    isLoading,
    memberTiles,
    memberRecordsByType,
    browserTiles,
    summaryDateFormat
  ]);

  const patientName = `${data?.firstName || ''} ${data?.lastName || ''}`.trim();
  const patientId = familyMemberId !== NO_MEMBER ? familyMemberId : '';

  const [isDisabled, setIsDisabled] = React.useState(false);
  const [newNote, setNewNote] = React.useState(false);

  // Open a blank record form. The RESET of `currentModel` is the load-bearing part, not a tidy-up:
  // that state survives between form opens, so without it a new record inherits whatever the previous
  // one left behind. That produced a real bug in QA — after recording a prenatal test for a fetus, the
  // next record added the ordinary way picked up the stale `pregnancyId`/`fetusId` and was labelled as
  // belonging to that fetus, i.e. a clinical misattribution on the MOTHER's own test.
  //
  // Every path that opens a NEW record goes through here so the invariant holds in one place: a new
  // record starts from a clean model unless a scope is deliberately supplied (see openScopedRecord).
  // See recordOpeners: the reset of `currentModel` is the load-bearing part, and it lives in one place
  // so the next path someone adds cannot forget it.
  const { openNewRecord, openScopedRecord } = React.useMemo(
    () =>
      makeRecordOpeners({
        setCurrentModel,
        setNoteType,
        setIsEditing,
        setNewNote,
        setIsDisabled,
        setOpenModal
      }),
    []
  );

  const addClicked = async () => {
    openNewRecord();
  };

  const [selectedRecordType, setSelectedRecordType] = useState('');

  // Honour a "record an event for this fetus" request. The scope is seeded into the model the form
  // opens with, so the new record carries `pregnancyId`/`fetusId` and is labelled as prenatal wherever
  // it is listed — rather than looking like something done to the mother.
  React.useEffect(() => {
    if (!addFetusEvent?.pregnancyId) return;
    openScopedRecord({
      type: addFetusEvent.noteType || 'Test',
      scope: {
        pregnancyId: addFetusEvent.pregnancyId,
        fetusId: addFetusEvent.fetusId
      }
    });
    onAddFetusEventHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addFetusEvent]);

  const handleRecordTypeSelect = (event) => {
    const value = event.target.value;
    if (value) {
      openNewRecord({ type: value });
      setSelectedRecordType('');
    }
  };

  const handleChanges = (key) => {
    let replace = { ...filter };
    replace[key] = !replace[key];
    setFilter(replace);
  };

  const [noteTypes] = React.useState([
    'Address',
    'Appointment',
    'Clinical Note',
    'Consent',
    'Contact',
    'Process',
    'Referral',
    'Sample',
    'Test',
    'Treatment',
    'Tissue Pathology',
    'Risk Factor'
  ]);

  const handleClose = () => {
    setUnsavedChanges({ status: false, edit: false });
    setNoteChangeCount(0);
    setOpenModal(false);
    setIsEditing(false);
    setNewNote(false);
    setLabReportInboxItemId(null);
    setLabReportInboxIndividual(null);
    setTestViewMode(undefined);
  };

  // Every way out of a record (its Cancel, the X, Escape, a click outside) asks
  // while it holds unsaved changes; a save or a delete passes `exit`.
  const handleNoteClose = (event) => {
    if (event?.exit) {
      handleClose();
    } else if (noteChangeCount > 0 || unsavedChanges.edit) {
      setUnsavedChanges((prev) => ({ ...prev, status: true }));
    } else handleClose();
  };

  const handleCancel = () => {
    setUnsavedChanges((prev) => ({ ...prev, status: false }));
  };

  // A reload, or a link out of the pedigree, asks too while the open record
  // holds changes.
  useUnsavedFormGuard(isModalOpen ? noteChangeCount : 0);

  // See THE TEST'S VERIFICATION VIEW above.
  const testDialogFullScreen =
    noteType === 'Test' &&
    (testViewMode === undefined
      ? !!labReportInboxItemId
      : testViewMode === 'review' || testViewMode === 'saved');

  const isProband = !!data?.proBandId;
  const getMemberKey = React.useCallback(
    (member) => memberMatchKeyFor(member),
    []
  );

  const handleSuggestionsAccepted = React.useCallback(
    ({ phenotypes, genotypes, diagnoses }) => {
      if (!setMemberRecords || !(phenotypes || genotypes || diagnoses)) return;
      setMemberRecords((prev) => {
        let updatedMember = null;

        const updated = prev.map((member) => {
          // In-memory match, so getMemberKey's chain (pid included) is correct
          // here — see memberMatchKey. String() on both sides because a pid is a
          // number and a UPN is a string.
          const memberId = getMemberKey(member);

          if (memberId !== memberMatchKey) return member;

          updatedMember = {
            ...member,
            ...(phenotypes && { phenotypes }),
            ...(genotypes && { genotypes }),
            ...(diagnoses && { diagnoses })
          };
          return updatedMember;
        });

        return updated;
      });
    },
    [setMemberRecords, getMemberKey, memberMatchKey]
  );

  // `withClinical`: also take each member's phenotypes, genotypes and
  // diagnoses — after Suggested Clinical Terms wrote to relatives as well as
  // to the person open here (TGCLOUD-1845).
  const refreshPedigreeLabels = React.useCallback(
    async (options) => {
      if (!familyId || !setMemberRecords) return;
      const withClinical = options?.withClinical === true;

      try {
        const response = await axios.get(
          APICONSTANTS.getPedigreeDetails.replace(':familyId', familyId)
        );
        const { proband, families = [] } = response.data || {};
        const refreshedMembers = [proband, ...families].filter(Boolean);
        const refreshedMap = new Map(
          refreshedMembers.map((member) => [getMemberKey(member), member])
        );

        const fmt = theme?.dateFormat || 'MM/DD/YYYY';

        setMemberRecords((prevRecords) =>
          prevRecords.map((member) => {
            const refreshedMember = refreshedMap.get(getMemberKey(member));
            if (!refreshedMember) return member;
            // Same shape the initial member build produces — an array, one entry
            // per consultation. This was a second copy of the map-and-join, so a
            // post-save refresh used to re-flatten what the build had just left as
            // rows and the label collapsed back to one wrapped blob until the
            // family was reloaded (TGCLOUD-1781).
            const consultationDate = formatMemberConsultationDates(
              refreshedMember,
              fmt
            );
            return {
              ...member,
              consultationDate,
              confirmedDiagnosis: refreshedMember.confirmedDiagnosis || '',
              tests: refreshedMember.tests || '',
              testRecords: refreshedMember.testRecords || [],
              testResults: refreshedMember.testResults || '',
              pedigreeText: refreshedMember.pedigreeText || '',
              ...(withClinical
                ? {
                    phenotypes: refreshedMember.phenotypes || [],
                    genotypes: refreshedMember.genotypes || [],
                    diagnoses: refreshedMember.diagnoses || []
                  }
                : {})
            };
          })
        );

        dispatchUpdate({
          type: EVENTS.SET_PEDIGREE_SELECTED_USER,
          value: {
            ...users,
            selectedUser: proband,
            family: families
          }
        });
      } catch (err) {
        console.error('Failed to refresh pedigree labels:', err);
      }
    },
    [dispatchUpdate, familyId, getMemberKey, setMemberRecords, theme, users]
  );

  // The family for Suggested Clinical Terms, so a relative's finding can be
  // added to that relative (TGCLOUD-1845).
  const suggestionFamily = React.useMemo(
    () => ({
      members: familyMembers,
      selected: data,
      refreshFamily: () => refreshPedigreeLabels({ withClinical: true })
    }),
    [familyMembers, data, refreshPedigreeLabels]
  );

  const forms = (type, model = {}) => {
    switch (type) {
      case 'Referral':
        return (
          <ReferralForm
            handleClose={handleNoteClose}
            familyMemberId={familyMemberId}
            isProband={isProband}
            isEditing={isEditing}
            model={model}
            data={data}
            isDisabled={isDisabled || isLocked}
            setUnsavedChanges={setUnsavedChanges}
            onChangeCount={setNoteChangeCount}
          />
        );
      case 'Appointment':
        return (
          <AppointmentForm
            handleClose={handleNoteClose}
            familyMemberId={familyMemberId}
            isProband={isProband}
            isEditing={isEditing}
            model={model}
            data={data}
            staffMembers={staffMembers}
            staffMemberOptions={staffMemberOptions}
            familyMembers={familyMembers}
            isDisabled={isDisabled || isLocked}
            setUnsavedChanges={setUnsavedChanges}
            onChangeCount={setNoteChangeCount}
            refreshPedigreeLabels={refreshPedigreeLabels}
          />
        );
      case 'Contact':
        return (
          <ContactForm
            handleClose={handleNoteClose}
            familyMemberId={familyMemberId}
            isEditing={isEditing}
            model={model}
            staffMembers={staffMembers}
            staffMemberOptions={staffMemberOptions}
            data={data}
            memberTests={memberTests}
            memberRecordsByType={memberRecordsByType}
            isDisabled={isDisabled || isLocked}
            setUnsavedChanges={setUnsavedChanges}
            onChangeCount={setNoteChangeCount}
          />
        );
      case 'Consent':
        return (
          <ConsentForm
            handleClose={handleNoteClose}
            familyMemberId={familyMemberId}
            isEditing={isEditing}
            model={model}
            data={data}
            staffMembers={staffMembers}
            staffMemberOptions={staffMemberOptions}
            setUnsavedChanges={setUnsavedChanges}
            onChangeCount={setNoteChangeCount}
          />
        );
      case 'Sample':
        return (
          <SampleForm
            handleClose={handleNoteClose}
            familyMemberId={familyMemberId}
            isEditing={isEditing}
            model={model}
            data={data}
            staffMembers={staffMembers}
            staffMemberOptions={staffMemberOptions}
            setUnsavedChanges={setUnsavedChanges}
            onChangeCount={setNoteChangeCount}
          />
        );
      case 'Test':
        return (
          <TestForm
            handleClose={handleNoteClose}
            data={data}
            familyMemberId={familyMemberId}
            isProband={isProband}
            isEditing={isEditing}
            model={model}
            staffMembers={staffMembers}
            staffMemberOptions={staffMemberOptions}
            setUnsavedChanges={setUnsavedChanges}
            onChangeCount={setNoteChangeCount}
            refreshPedigreeLabels={refreshPedigreeLabels}
            labReportInboxItemId={labReportInboxItemId}
            labReportInboxIndividual={labReportInboxIndividual}
            onVerificationModeChange={setTestViewMode}
          />
        );
      case 'Treatment':
        return (
          <TreatmentForm
            handleClose={handleNoteClose}
            familyMemberId={familyMemberId}
            isProband={isProband}
            isEditing={isEditing}
            model={model}
            data={data}
            setUnsavedChanges={setUnsavedChanges}
            onChangeCount={setNoteChangeCount}
          />
        );
      case 'Address':
        return (
          <AddressForm
            handleClose={handleNoteClose}
            familyId={familyId}
            familyMemberId={familyMemberId}
            isEditing={isEditing}
            model={model}
            setUnsavedChanges={setUnsavedChanges}
            onChangeCount={setNoteChangeCount}
          />
        );
      case 'Clinical Note':
        return (
          <ClinicalNotesForm
            handleClose={handleNoteClose}
            familyMemberId={familyMemberId}
            isProband={isProband}
            isEditing={isEditing}
            model={model}
            data={data}
            staffMembers={staffMembers}
            staffMemberOptions={staffMemberOptions}
            isDisabled={isDisabled || isLocked}
            setUnsavedChanges={setUnsavedChanges}
            onChangeCount={setNoteChangeCount}
            onSuggestionsAccepted={handleSuggestionsAccepted}
          />
        );
      case 'Tissue Pathology':
        return (
          <TissuePathologyForm
            handleClose={handleNoteClose}
            familyMemberId={familyMemberId}
            isProband={isProband}
            isEditing={isEditing}
            model={model}
            data={data}
            staffMembers={staffMembers}
            staffMemberOptions={staffMemberOptions}
            setUnsavedChanges={setUnsavedChanges}
            onChangeCount={setNoteChangeCount}
          />
        );
      case 'Process':
        return (
          <ProcessForm
            handleClose={handleNoteClose}
            familyMemberId={familyMemberId}
            isEditing={isEditing}
            model={model}
            data={data}
            staffMembers={staffMembers}
            staffMemberOptions={staffMemberOptions}
            isDisabled={isDisabled || isLocked}
            setUnsavedChanges={setUnsavedChanges}
            onChangeCount={setNoteChangeCount}
            setMemberRecords={setMemberRecords}
          />
        );
      case 'Risk Factor':
        return (
          <RiskFactorForm
            handleClose={handleNoteClose}
            familyMemberId={familyMemberId}
            isProband={isProband}
            isEditing={isEditing}
            model={model}
            data={data}
            isDisabled={isDisabled || isLocked}
            setUnsavedChanges={setUnsavedChanges}
            onChangeCount={setNoteChangeCount}
          />
        );
      default:
        return <></>;
    }
  };

  const fetchStaffMembers = () => {
    axios.get(`/clinical-notes/staff-members`).then((res) => {
      const parsedStaffMembers = res.data.map((member) => ({
        firstName: member.firstName,
        lastName: member.lastName,
        userId: member._id
      }));
      setStaffMembers(parsedStaffMembers || []);
    });
  };

  React.useEffect(() => {
    fetchStaffMembers();
  }, []);

  const doFetch = React.useCallback(() => {
    setIsLoading(true);

    return axios
      .get(`/clinical-notes/all`, {
        params: { familyId }
      })
      .then((res) => {
        let edited = res.data;
        // Sort the notes based on the pinned property
        edited.sort(function (a) {
          if (a.pinned) return -1;
          return 1;
        });

        // Transform the data based on noteType
        const transformedData = edited.map((note) => {
          // Handle Consent type note - keep obtainedBy as string (same as Sample)
          if (note.noteType === 'Consent') {
            if (Array.isArray(note.obtainedBy) && note.obtainedBy.length > 0) {
              const s = note.obtainedBy[0];
              note.obtainedBy = `${s.firstName ?? ''} ${
                s.lastName ?? ''
              }`.trim();
            }
            // If already a string, keep it as is
          }
          if (note.noteType === 'Test') {
            note.requestedBy =
              Array.isArray(note.requestedBy) && note.requestedBy.length > 0
                ? note.requestedBy[0].userId
                : null;
          }
          if (note.noteType === 'Appointment') {
            note.staffMember =
              Array.isArray(note.staffMember) && note.staffMember.length > 0
                ? note.staffMember.map((member) => member.userId)
                : null;
          }
          if (note.noteType === 'Contact') {
            note.staffMember =
              Array.isArray(note.staffMember) && note.staffMember.length > 0
                ? note.staffMember.map((member) => member.userId)
                : null;

            note.requestChecking =
              Array.isArray(note.requestChecking) &&
              note.requestChecking.length > 0
                ? note.requestChecking[0].userId
                : null;
          }
          if (note.noteType === 'Tissue Pathology') {
            note.staffMember =
              Array.isArray(note.staffMember) && note.staffMember.length > 0
                ? note.staffMember.map((member) => member.userId)
                : [];
            note.pathologyDoctor =
              Array.isArray(note.pathologyDoctor) &&
              note.pathologyDoctor.length > 0
                ? note.pathologyDoctor[0].userId
                : '';
          }
          if (note.noteType === 'Process') {
            note.staffMember =
              Array.isArray(note.staffMember) && note.staffMember.length > 0
                ? note.staffMember.map((member) => member.userId)
                : [];
          }
          return note;
        });
        setTiles(transformedData);

        setIsLoading(false);
        setRecordsLoadedFor(familyId);
        // Auto-open a clinical record modal when a record ID is present in the URL. Not for the lab
        // report inbox's link: that one waits for the person to be selected (see its step below).
        if (
          familyMemberIdFromURL &&
          !isOpenedModal.current &&
          !labReportInboxInURL
        ) {
          const urlRecordMap = [
            { id: appointmentIdFromURL, noteType: 'Appointment' },
            { id: referralIdFromURL, noteType: 'Referral' },
            { id: contactIdFromURL, noteType: 'Contact' },
            { id: sampleIdFromURL, noteType: 'Sample' },
            { id: testIdFromURL, noteType: 'Test' },
            { id: clinicalNoteIdFromURL, noteType: 'Clinical Note' },
            { id: consentIdFromURL, noteType: 'Consent' }
          ];

          for (const { id, noteType: type } of urlRecordMap) {
            if (!id) continue;
            const record = transformedData.find(
              (data) =>
                data.familyMemberId === familyMemberIdFromURL && data._id === id
            );
            if (record) {
              setIsEditing(true);
              setCurrentModel(record);
              setNoteType(type);
              setOpenModal(true);
              isOpenedModal.current = true;
              break;
            }
          }
        }
      })
      .catch((err) => {
        console.error('Error fetching clinical notes:', err);
        setIsLoading(false);
      });
    // eslint-disable-next-line
  }, [
    familyId,
    familyMemberIdFromURL,
    appointmentIdFromURL,
    referralIdFromURL,
    contactIdFromURL,
    sampleIdFromURL,
    testIdFromURL,
    clinicalNoteIdFromURL,
    consentIdFromURL,
    labReportInboxInURL
  ]);
  const returningFromEditor = React.useRef(false);

  React.useEffect(() => {
    // Only fetch when the modal is closed — avoids a race condition where an
    // in-flight fetch from modal-open completes *after* the post-save fetch
    // and overwrites tiles with stale (pre-save) data.
    if (isModalOpen) {
      returningFromEditor.current = true;
      return;
    }
    const cameFromEditor = returningFromEditor.current;
    returningFromEditor.current = false;
    if (!cameFromEditor) setTiles([]);
    doFetch();
    // TGCLOUD-1670 — the pedigree data overlays read the same records this list
    // does. Closing the record modal is the one save path that refetches HERE
    // without going through the `clinical-notes-refresh` window event, so
    // without this signal a clinician adds the contact an overlay has just told
    // them is missing and the mark stays on the node. A distinct event rather
    // than reusing `clinical-notes-refresh`, which this component listens to
    // itself and would answer with a second, redundant fetch.
    window.dispatchEvent(new CustomEvent('clinical-records-changed'));
  }, [isModalOpen, doFetch]);

  React.useEffect(() => {
    window.addEventListener('clinical-notes-refresh', doFetch);
    return () => window.removeEventListener('clinical-notes-refresh', doFetch);
  }, [doFetch]);

  const openTestRecord = React.useCallback((record) => {
    const normalized = { ...record };
    if (
      Array.isArray(normalized.requestedBy) &&
      normalized.requestedBy.length > 0
    ) {
      normalized.requestedBy = normalized.requestedBy[0].userId;
    }
    setIsEditing(true);
    setNewNote(false);
    setCurrentModel(normalized);
    setNoteType('Test');
    setIsDisabled(false);
    setOpenModal(true);
  }, []);

  const openSourceTestRecord = React.useCallback(
    async ({ testId } = {}) => {
      if (!testId) return false;
      const cached = tiles.find(
        (t) => t.noteType === 'Test' && String(t._id) === String(testId)
      );
      if (cached) {
        openTestRecord(cached);
        return true;
      }
      try {
        const { data: record } = await axios.get(
          `/clinical-notes/tests/${testId}`
        );
        if (record?._id) {
          openTestRecord(record);
          return true;
        }
      } catch (err) {
        console.error('Failed to load source test record', err);
      }
      return false;
    },
    [tiles, openTestRecord]
  );

  // The lab report inbox's link, acted on once: the person's Test (or a new one) opened with the
  // inbox item when the chart has selected them, and the item then taken out of the address (the
  // router state kept) so a reload does not hand the report over a second time.
  //
  // For one person of a report about several (TGCLOUD-1868, Phase E4), the item goes with whom it
  // is for, and the Test is one they fit (labReportInboxLink.individualTestMisfit): a fetus's NEW
  // Test is opened scoped to its fetus record, by the same path as "record an event for this fetus"
  // (openScopedRecord), so it carries `pregnancyId` and `fetusId` like any Test added for a fetus;
  // a Test that does not fit is not opened, and the clinician is told why.
  const inboxLinkStep = labReportInboxLinkStep({
    link: labReportInboxLink,
    selectedAddress: familyMemberId,
    recordsLoaded: recordsLoadedFor === familyId && !isLoading,
    records: tiles
  });
  React.useEffect(() => {
    const link = labReportInboxLink;
    if (!link || handledInboxItem.current === link.itemId) return;
    if (inboxLinkStep.kind === 'wait' || inboxLinkStep.kind === 'none') return;
    handledInboxItem.current = link.itemId;
    // The record list must not open the same Test again when it reloads.
    isOpenedModal.current = true;
    if (inboxLinkStep.kind === 'open-new') {
      setLabReportInboxItemId(link.itemId);
      setLabReportInboxIndividual(link.individual || null);
      if (inboxLinkStep.scope) {
        openScopedRecord({ type: 'Test', scope: inboxLinkStep.scope });
      } else {
        openNewRecord({ type: 'Test' });
      }
    } else if (inboxLinkStep.kind === 'open-existing') {
      setLabReportInboxItemId(link.itemId);
      setLabReportInboxIndividual(link.individual || null);
      openTestRecord(inboxLinkStep.record);
    } else if (inboxLinkStep.kind === 'unfit') {
      setSnack?.({
        status: true,
        severity: 'warning',
        msg: t(UNFIT_LINK_MESSAGES[inboxLinkStep.reason])
      });
    } else {
      setSnack?.({
        status: true,
        severity: 'warning',
        msg: t(
          'The Test this lab report was to be applied to was not found for this person. Open it from the Lab Report Inbox again.'
        )
      });
    }
    navigate(
      {
        pathname: location.pathname,
        search: withoutLabReportInboxItem(location.search)
      },
      { replace: true, state: location.state }
    );
    // The step's kind and record decide; the rest is read as it stands when they change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labReportInboxLink, inboxLinkStep.kind, inboxLinkStep.record]);

  React.useEffect(() => {
    const handler = (e) => {
      const testId = e?.detail?.testId;
      if (!testId) return;
      openSourceTestRecord({ testId });
    };
    window.addEventListener('open-source-test-record', handler);
    return () => window.removeEventListener('open-source-test-record', handler);
  }, [openSourceTestRecord]);

  React.useEffect(() => {
    const handler = (e) => {
      const preselect = e?.detail?.model;
      setIsEditing(false);
      setCurrentModel(preselect ? { recordType: preselect } : {});
      setNoteType('Risk Factor');
      setNewNote(true);
      setIsDisabled(false);
      setOpenModal(true);
    };
    window.addEventListener('open-risk-factor-modal', handler);
    return () => window.removeEventListener('open-risk-factor-modal', handler);
  }, []);

  //Fix empty form selection values, shouldn't be necessary with radio
  React.useEffect(() => {
    if (noteType === '') setNoteType('Referral');
  }, [noteType]);
  const personSummary = React.useCallback(
    () =>
      formatPersonSummary(
        recordSubject({
          isNew: newNote || !isEditing,
          record: currentModel,
          selectedMember: data,
          members: familyMembers
        })
      ),
    [currentModel, newNote, isEditing, data, familyMembers]
  );

  const openRecordFromBrowser = React.useCallback(
    (record) => {
      const route = routeForType(record?.noteType);
      if (!route) return;

      if (route.kind === 'dialog') {
        setIsEditing(true);
        setNewNote(false);
        setCurrentModel(record);
        setNoteType(route.noteType);
        setIsDisabled(false);
        setOpenModal(true);
        return;
      }

      setIsBrowserOpen(false);

      if (route.kind === 'pregnancy') {
        onNavigateToSection?.(route.section, {
          [route.sidebarKey]: record._id
        });
        return;
      }

      onNavigateToSection?.(route.section);
      window.dispatchEvent(
        new CustomEvent(route.event, {
          detail: { id: record.id, _id: record._id, familyMemberId }
        })
      );
    },
    [onNavigateToSection, familyMemberId]
  );

  const createRecordFromBrowser = React.useCallback(
    (type) => {
      const route = routeForType(type);
      setIsBrowserOpen(false);
      if (!route) return;
      if (route.kind === 'dialog') {
        openNewRecord({ type });
        return;
      }
      onNavigateToSection?.(route.section);
      window.dispatchEvent(
        new CustomEvent(route.event, {
          detail: { create: true, familyMemberId }
        })
      );
    },
    [onNavigateToSection, openNewRecord, familyMemberId]
  );

  const withoutHiddenTypes = React.useCallback(
    (types) => types.filter((t) => !(anonymousActive && t === 'Address')),
    [anonymousActive]
  );
  const browserCreatableTypes = React.useMemo(
    () => withoutHiddenTypes(CREATABLE_TYPES),
    [withoutHiddenTypes]
  );
  const browserStarterTypes = React.useMemo(
    () => withoutHiddenTypes(CREATABLE_FROM_EMPTY),
    [withoutHiddenTypes]
  );

  const renderTiles = () => {
    const MemberCards = [];
    const FamilyCards = [];
    const sorted = [...tiles, ...riskTiles].sort((a, b) => {
      const da = new Date(getRecordDate(a) || a.createdAt || 0);
      const db = new Date(getRecordDate(b) || b.createdAt || 0);
      return sortOrder === 'newest' ? db - da : da - db;
    });
    sorted.forEach((item) => {
      const tileType =
        item.noteType === 'Risk Score' && item.riskLabel
          ? 'Risk Factor'
          : item.noteType;
      const riskLabel =
        item.noteType === 'Risk Score' ? item.riskLabel : undefined;
      if (filter[item.noteType] && isRecordForMember(item, familyMemberId))
        MemberCards.push(
          <TileCard
            model={item}
            key={item._id}
            type={tileType}
            riskLabel={riskLabel}
            setIsEditing={setIsEditing}
            setModalOpen={setOpenModal}
            setNoteType={setNoteType}
            setCurrentModel={setCurrentModel}
            deepCopy={familyMembers}
            staffMemberOptions={staffMemberOptions}
            carrier={data}
            personLabel={personLabel}
            onCustomClick={
              item.noteType === 'Risk Score'
                ? () => {
                    const orig = riskRecords.find((r) => r._id === item._id);
                    if (!orig) return;
                    setIsEditing(true);
                    setCurrentModel(orig);
                    setNoteType('Risk Factor');
                    setOpenModal(true);
                  }
                : undefined
            }
          />
        );

      if (item.showInFamily)
        FamilyCards.push(
          <TileCard
            model={item}
            key={item._id}
            showInFamily={item.showInFamily}
            highlightedPerson={data}
            type={tileType}
            riskLabel={riskLabel}
            setIsEditing={setIsEditing}
            setModalOpen={setOpenModal}
            setNoteType={setNoteType}
            setCurrentModel={setCurrentModel}
            deepCopy={familyMembers}
            staffMemberOptions={staffMemberOptions}
            setIsDisabled={setIsDisabled}
            carrier={data}
          />
        );
    });
    return { MemberCards, FamilyCards };
  };

  return (
    <div className="user-form">
      {/* TGCLOUD-1758 — a person who exists on the canvas but has never been
          saved has no UPN to address a record by, so record creation is not
          offered. Say why: the control simply vanishing reads as a permissions
          problem or a broken panel, and a clinician has no way to guess that
          saving the pedigree is what unlocks it. Wording matches the backend's
          400 for the same condition. */}
      {memberIsUnsaved && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t(
            'This person has not been saved yet. Save the pedigree to add clinical records for them.'
          )}
        </Alert>
      )}
      {familyMemberId !== NO_MEMBER && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl sx={{ minWidth: 250 }} disabled={isLocked}>
            <Select
              data-va-anchor="pedigree.clinicalRecords.typePicker"
              value={selectedRecordType}
              onChange={handleRecordTypeSelect}
              input={<OutlinedInput />}
              displayEmpty
              renderValue={(selected) => {
                if (selected === '') {
                  return (
                    <span style={{ color: '#999' }}>
                      {t('Create New Clinical Record')}
                    </span>
                  );
                }
                return selected;
              }}
            >
              <MenuItem disabled value="">
                <em>{t('Create New Clinical Record')}</em>
              </MenuItem>
              {noteTypes
                // Address records hold patient PII — not creatable on an
                // anonymous pedigree (backend rejects the route too).
                .filter((item) => !(anonymousActive && item === 'Address'))
                .map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          paddingTop: '1rem',
          gap: 1
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center'
          }}
        >
          <Tooltip title={t('Browse all records')}>
            <span>
              <IconButton
                ref={browseAllRef}
                size="small"
                onClick={() => setIsBrowserOpen(true)}
                aria-label={t('Browse all clinical records')}
                // TGCLOUD-1669 — no longer disabled at zero records. The old
                // condition also gated on `tiles`, which is the whole FAMILY's
                // notes, so it disabled a member with no records only when no
                // relative had any either. Either way, the moment a clinician
                // most needs a way to add the first record is exactly when
                // there are none.
              >
                <ManageSearchIcon fontSize="medium" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              sortOrder === 'newest' ? t('Oldest first') : t('Newest first')
            }
          >
            <IconButton
              size="small"
              onClick={() =>
                setSortOrder((s) => (s === 'newest' ? 'oldest' : 'newest'))
              }
              aria-label={t('Toggle sort order')}
            >
              <SwapVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <FilterClinicalRecord filter={filter} setFilter={setFilter} />
        </Box>
        {isLoading ? (
          <Chip label={t('Loading')} />
        ) : renderTiles().MemberCards.length === 0 ? (
          <Chip label={t('No Notes Found!')} />
        ) : (
          <>
            {renderTiles().MemberCards.map((tile, iter) => (
              <Grid key={iter} size={12}>
                {tile}
              </Grid>
            ))}
          </>
        )}
      </Box>
      {!isLoading && (
        <Box sx={{ marginTop: 4 }}>
          <hr />
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          paddingTop: '1rem',
          gap: 1
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <FormLabel component="label" className="form-label">
            {t('Family Clinical Records')}
          </FormLabel>
          <IconButton
            onClick={() =>
              modificationHistoryHandle(
                CONSTANTS.auditLogType['clinicalRecord'],
                filter
              )
            }
          >
            <HistoryIcon />
          </IconButton>
        </Box>

        {renderTiles().FamilyCards.map((tile, iter) => (
          <Grid key={iter} size={12}>
            {tile}
          </Grid>
        ))}

        {renderTiles().FamilyCards.length === 0 && (
          <Chip label={t('No Notes Found!')} />
        )}
      </Box>
      <UnsavedChangesDialog
        open={unsavedChanges.status}
        onCancel={handleCancel}
        onDiscard={handleClose}
        count={noteChangeCount}
      />
      <CustomDialog
        maxWidth={
          noteType === 'Risk Factor' ? 'lg' : CLINICAL_RECORD_DIALOG_WIDTH
        }
        fullScreen={testDialogFullScreen}
        title={
          <ModalHeader
            title={
              isEditing
                ? t('Edit {{noteType}}', { noteType })
                : t('Create New {{noteType}}', { noteType })
            }
            personSummary={personSummary}
          />
        }
        allowBackgroundClose={true}
        open={isModalOpen}
        handleClose={handleNoteClose}
        minHeight="auto"
        onClose={true}
        footerAction={
          isEditing && currentModel?._id && modificationHistoryHandle ? (
            <IconButton
              size="small"
              title={t('Record history')}
              onClick={() =>
                modificationHistoryHandle(
                  CONSTANTS.auditLogType.clinicalRecord,
                  {},
                  currentModel._id
                )
              }
            >
              <HistoryIcon />
            </IconButton>
          ) : null
        }
      >
        <div
          data-va-anchor="pedigree.clinicalRecords.dialog"
          // Full screen, the Test's two panes fill the dialog: the same element, only its style.
          style={testDialogFullScreen ? FULL_SCREEN_BODY_STYLE : undefined}
        >
          <SuggestionFamilyContext.Provider value={suggestionFamily}>
            {forms(noteType, currentModel)}
          </SuggestionFamilyContext.Provider>
        </div>
      </CustomDialog>
      {dockNode &&
        createPortal(
          <RecordBrowser
            tiles={browserTiles}
            isLoading={isLoading}
            staffMemberOptions={staffMemberOptions}
            carrier={data}
            personLabel={personLabel}
            facetLayout="strip"
            // Only ONE browser binds the window key handling at a time. With
            // the dialog open over the top, the arrow keys belong to it.
            active={!isBrowserOpen}
            onCreateRecord={createRecordFromBrowser}
            creatableTypes={browserCreatableTypes}
            starterTypes={browserStarterTypes}
            onOpenRecord={openRecordFromBrowser}
            canOpenRecord={canOpenType}
            keyboardSuspended={isModalOpen}
          />,
          dockNode
        )}

      <RecordBrowserModal
        open={isBrowserOpen}
        onClose={() => {
          setIsBrowserOpen(false);
          browseAllRef.current?.focus();
        }}
        tiles={browserTiles}
        onCreateRecord={createRecordFromBrowser}
        creatableTypes={browserCreatableTypes}
        starterTypes={browserStarterTypes}
        onOpenRecord={openRecordFromBrowser}
        canOpenRecord={canOpenType}
        keyboardSuspended={isModalOpen}
        isLoading={isLoading}
        patientName={patientName}
        patientId={patientId}
        patientDob={data?.dob}
        staffMemberOptions={staffMemberOptions}
        carrier={data}
        personLabel={personLabel}
      />
    </div>
  );
}

const FilterClinicalRecord = ({ filter, setFilter }) => {
  const t = useAutoTranslation();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const allOptions = Object.keys(filter).sort((a, b) => a.localeCompare(b));
  const selectedValues = allOptions.filter((key) => filter[key]);
  const selectedText =
    selectedValues.length > 0 && selectedValues.length < allOptions.length
      ? t('{{count}} selected', { count: selectedValues.length })
      : '';

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = (name) => {
    setFilter((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleSelectAll = (event) => {
    event.stopPropagation();
    const newFilter = { ...filter };
    Object.keys(newFilter).forEach((key) => {
      newFilter[key] = true;
    });
    setFilter(newFilter);
  };

  const handleClearAll = (event) => {
    event.stopPropagation();
    const newFilter = { ...filter };
    Object.keys(newFilter).forEach((key) => {
      newFilter[key] = false;
    });
    setFilter(newFilter);
  };

  return (
    <div>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton
          size="small"
          onClick={handleOpen}
          aria-label={t('Filter clinical records')}
        >
          <FilterListIcon />
        </IconButton>
        <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
          {selectedText}
        </Box>
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: { sx: { p: 1, minWidth: 260 } }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Button onClick={handleSelectAll} size="small">
            {t('Select All')}
          </Button>
          <Button onClick={handleClearAll} size="small">
            {t('Clear All')}
          </Button>
        </Box>
        <MenuList disablePadding>
          {allOptions.map((name) => (
            <MenuItem
              key={name}
              onClick={() => handleToggle(name)}
              sx={{ py: 0.5 }}
            >
              <Checkbox checked={filter[name]} size="small" />
              <ListItemText primary={name} />
            </MenuItem>
          ))}
        </MenuList>
      </Popover>
    </div>
  );
};

export default ClinicalNotes;
